/**
 * @fileoverview Atom: a ring that fills as a countdown runs out.
 * @module components/atoms/ProgressRing
 */

import "./ProgressRing.css"

/** Props accepted by {@link ProgressRing}. */
export interface ProgressRingProps {
	/** Share of the countdown already elapsed, 0 to 1. */
	readonly ratio: number
	/** Outer diameter in pixels. Defaults to 28. */
	readonly size?: number
}

/** Stroke width, as a share of the diameter. */
const STROKE_RATIO = 0.14

/**
 * Countdown ring.
 *
 * @param props - Elapsed share and diameter.
 * @returns The rendered ring, hidden from assistive technology: the countdown
 *   it illustrates is announced as text by its container.
 *
 * @example
 * <ProgressRing ratio={cooldown.ratio} />
 */
export function ProgressRing({ ratio, size = 28 }: ProgressRingProps) {
	const stroke = size * STROKE_RATIO
	const radius = (size - stroke) / 2
	const circumference = 2 * Math.PI * radius
	const clamped = Math.min(1, Math.max(0, ratio))

	return (
		<svg
			className="progress-ring"
			width={size}
			height={size}
			viewBox={`0 0 ${String(size)} ${String(size)}`}
			aria-hidden="true"
		>
			<circle
				className="progress-ring__track"
				cx={size / 2}
				cy={size / 2}
				r={radius}
				strokeWidth={stroke}
			/>
			<circle
				className="progress-ring__fill"
				cx={size / 2}
				cy={size / 2}
				r={radius}
				strokeWidth={stroke}
				strokeDasharray={circumference}
				strokeDashoffset={circumference * (1 - clamped)}
				transform={`rotate(-90 ${String(size / 2)} ${String(size / 2)})`}
			/>
		</svg>
	)
}
